import React from 'react';
import { Search, Phone, ShieldCheck } from 'lucide-react';

const HowItWorks = () => {
  const scenarios = [
    {
      label: 'Scenario 1 — You Need to Travel',
      emoji: '✈️',
      labelColor: 'bg-orange-100 text-orange-700',
      steps: [
        {
          number: '01',
          icon: <Search size={22} className="text-brand-orange" />,
          bg: 'bg-orange-50 border-orange-100',
          title: 'Tell Us Your City & Pet Type',
          desc: 'Enter your location in Coimbatore (e.g., RS Puram, Gandhipuram). Select your pet — dog, cat, bird, or other. We filter shops that specifically handle your pet type.',
        },
        {
          number: '02',
          icon: <ShieldCheck size={22} className="text-brand-teal" />,
          bg: 'bg-teal-50 border-teal-100',
          title: 'Browse Verified Boarding Centres',
          desc: 'See a curated list of trusted local pet shops, boarding homes, and daycare centres near you — each personally verified by our team with ratings, services, and operating hours.',
        },
        {
          number: '03',
          icon: <Phone size={22} className="text-green-600" />,
          bg: 'bg-green-50 border-green-100',
          title: 'Call & Confirm. Then Travel Easy.',
          desc: 'Contact the shop directly with one tap. No middlemen, no booking fees. Confirm your pet\'s stay and leave for your trip knowing your furry friend is in trusted hands.',
        },
      ],
    },
    {
      label: 'Scenario 2 — Your Pet Needs Urgent Help',
      emoji: '🚨',
      labelColor: 'bg-red-100 text-red-700',
      steps: [
        {
          number: '01',
          icon: <Search size={22} className="text-emergency-red" />,
          bg: 'bg-red-50 border-red-100',
          title: 'Spot the Emergency',
          desc: 'Your pet is injured, not eating, showing unusual symptoms, or in any kind of distress. Every minute matters. Open PetCares immediately.',
        },
        {
          number: '02',
          icon: <ShieldCheck size={22} className="text-emergency-red" />,
          bg: 'bg-red-50 border-red-100',
          title: 'See Vets Available Right Now',
          desc: 'We show you a live list of licensed veterinarians near you — with their specialty, distance, availability, and direct phone number. No appointment booking needed.',
        },
        {
          number: '03',
          icon: <Phone size={22} className="text-emergency-red" />,
          bg: 'bg-red-50 border-red-100',
          title: 'Call & Get There Fast',
          desc: 'Call the vet with one tap. They are expecting emergencies. You get real guidance immediately — whether to rush in or be guided over the phone while you travel to the clinic.',
        },
      ],
    },
  ];

  return (
    <section id="how-it-works" className="py-24 bg-gray-50 border-y border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block text-brand-teal font-bold tracking-widest uppercase text-xs mb-3 bg-teal-50 px-4 py-1.5 rounded-full border border-teal-100">
            How PetCares Works
          </span>
          <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-5">
            Two Problems.<br />
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-brand-orange to-brand-teal">
              One Simple Platform.
            </span>
          </h2>
          <p className="text-xl text-gray-500 leading-relaxed">
            PetCares was built to solve exactly two situations every pet owner in South India faces. Here is how it works for each one.
          </p>
        </div>

        {/* Feature Image */}
        <div className="max-w-5xl mx-auto mb-20 rounded-[2rem] overflow-hidden shadow-2xl border-4 border-white h-72 lg:h-[450px] relative group">
          <img src={require('../assets/images/app_usage.png')} alt="Using PetCares Platform" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent opacity-60" />
          <div className="absolute bottom-6 left-6 text-white text-left">
            <h3 className="font-extrabold text-2xl mb-1">Seamless Pet Care</h3>
            <p className="text-sm text-gray-200">Connect with local pet professionals in seconds.</p>
          </div>
        </div>

        <div className="space-y-16">
          {scenarios.map((scenario, si) => (
            <div key={si}>
              <div className="flex items-center gap-3 mb-8">
                <span className="text-2xl">{scenario.emoji}</span>
                <span className={`text-sm font-bold px-4 py-1.5 rounded-full ${scenario.labelColor}`}>{scenario.label}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative">
                {/* Connector line */}
                <div className="hidden md:block absolute top-10 left-[calc(16.67%-1px)] right-[calc(16.67%-1px)] h-px bg-gray-200 z-0" />
                {scenario.steps.map((step, i) => (
                  <div key={i} className={`relative z-10 bg-white rounded-3xl border ${step.bg} p-7 shadow-sm hover:shadow-md transition-all`}>
                    <div className="flex items-center justify-between mb-5">
                      <div className={`w-12 h-12 rounded-2xl border flex items-center justify-center ${step.bg}`}>
                        {step.icon}
                      </div>
                      <span className="text-4xl font-black text-gray-100 select-none">{step.number}</span>
                    </div>
                    <h4 className="text-lg font-bold text-gray-900 mb-3">{step.title}</h4>
                    <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
